/**
 * MarkingQueue.jsx — Phase 2C C2: Marker Work Queue
 *
 * Shows only cases assigned to the authenticated marker.
 * Role-sensitive action labels (Start / Continue / View).
 * Student access → 403 via EvaluationShell.
 */
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ClipboardList,
  RefreshCw,
  Lock,
  Play,
  ArrowRight,
  BadgeAlert,
  Info,
} from "lucide-react";
import { toast } from "sonner";
import { EvaluationShell } from "../components/evaluation/EvaluationShell";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
function authH() {
  return { Authorization: `Bearer ${localStorage.getItem("academiaos_token")}` };
}
async function apiFetch(path, opts = {}) {
  const r = await fetch(`${API}${path}`, {
    headers: { ...authH(), "Content-Type": "application/json" },
    ...opts,
  });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(body?.detail?.message || String(body?.detail || r.statusText)), { status: r.status });
  return body;
}

const COORD_ROLES = new Set(["super_admin", "institution_admin", "programme_manager", "registrar", "exam_admin"]);

// Derive the call-to-action from case status + actor's marker_role
function caseAction(status, markerRole) {
  if (markerRole === "PRIMARY_MARKER") {
    if (status === "ASSIGNED")            return { label: "Start Marking",    variant: "primary", icon: Play };
    if (status === "MARKING_IN_PROGRESS") return { label: "Continue Marking", variant: "primary", icon: ArrowRight };
    return { label: "View (Submitted)", variant: "ghost", icon: Lock };
  }
  if (markerRole === "SECOND_MARKER") {
    if (status === "SECOND_MARK_REQUIRED") return { label: "Start Second Mark", variant: "primary", icon: Play };
    if (status === "MARKING_IN_PROGRESS")  return { label: "Continue Marking",  variant: "primary", icon: ArrowRight };
    return { label: "View (Submitted)", variant: "ghost", icon: Lock };
  }
  if (markerRole === "MODERATOR") {
    if (status === "MODERATION_REQUIRED") return { label: "Start Moderation", variant: "amber", icon: Play };
    if (status === "MODERATED")           return { label: "View (Resolved)",  variant: "ghost", icon: Lock };
    return { label: "View",              variant: "ghost", icon: ArrowRight };
  }
  return { label: "Open", variant: "ghost", icon: ArrowRight };
}

const STATUS_BADGE = {
  UNASSIGNED:           "bg-slate-800 text-slate-400 border-slate-700",
  ASSIGNED:             "bg-indigo-950 text-indigo-300 border-indigo-800/60",
  MARKING_IN_PROGRESS:  "bg-sky-950 text-sky-300 border-sky-800/60",
  MARKED:               "bg-teal-950 text-teal-300 border-teal-800/60",
  SECOND_MARK_REQUIRED: "bg-amber-950 text-amber-300 border-amber-800/60",
  SECOND_MARKED:        "bg-lime-950 text-lime-300 border-lime-800/60",
  MODERATION_REQUIRED:  "bg-orange-950 text-orange-300 border-orange-800/60",
  MODERATED:            "bg-violet-950 text-violet-300 border-violet-800/60",
  READY_FOR_RATIFICATION: "bg-emerald-950 text-emerald-300 border-emerald-800/60",
  RATIFIED:             "bg-green-950 text-green-300 border-green-800/60",
  PUBLISHED:            "bg-cyan-950 text-cyan-300 border-cyan-800/60",
};

function StatusBadge({ status }) {
  const cls = STATUS_BADGE[status] || "bg-slate-800 text-slate-400 border-slate-700";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${cls}`}>
      {status?.replace(/_/g, " ")}
    </span>
  );
}

export default function MarkingQueue() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [assignments, setAssignments] = useState({}); // caseId → assignment row
  const [loading, setLoading] = useState(true);

  const isCoordinator = COORD_ROLES.has(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch("/evaluation/cases");
      setCases(data.cases || []);
    } catch (e) {
      toast.error("Failed to load cases: " + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openWorkspace = (caseId, status) => {
    if (status === "MODERATION_REQUIRED" || status === "MODERATED") {
      navigate(`/assessment-evaluation/moderation/${caseId}`);
    } else {
      navigate(`/assessment-evaluation/marking/${caseId}`);
    }
  };

  const RELEVANT_STATUSES = new Set([
    "ASSIGNED", "MARKING_IN_PROGRESS", "MARKED",
    "SECOND_MARK_REQUIRED", "SECOND_MARKED",
    "MODERATION_REQUIRED", "MODERATED",
  ]);

  const visibleCases = isCoordinator ? cases : cases.filter((c) => RELEVANT_STATUSES.has(c.status));

  return (
    <EvaluationShell title="Marking" breadcrumb="Marking">
      {/* Header strip */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-400 mt-1">
            {isCoordinator
              ? "All evaluation cases in scope."
              : "Cases assigned to you. Only your marking rounds are accessible."}
          </p>
        </div>
        <button
          data-testid="refresh-queue-btn"
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-700 text-slate-400 text-sm hover:border-slate-600 hover:text-slate-200 disabled:opacity-40 transition-colors"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 gap-3 text-slate-500">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span className="text-sm">Loading your assigned cases…</span>
        </div>
      ) : visibleCases.length === 0 ? (
        <div
          data-testid="empty-queue"
          className="flex flex-col items-center justify-center py-20 text-slate-500"
        >
          <ClipboardList className="w-10 h-10 mb-3 opacity-30" />
          <div className="text-sm font-medium">No cases in your queue</div>
          <div className="text-xs mt-1 text-slate-600">
            Cases appear here once a Coordinator assigns you as a marker.
          </div>
        </div>
      ) : (
        <div className="space-y-2" data-testid="marking-queue-list">
          {visibleCases.map((c) => {
            // Derive assumed marker role from case status for action button labelling
            let inferredRole = "PRIMARY_MARKER";
            if (c.status === "SECOND_MARK_REQUIRED" || c.status === "SECOND_MARKED") inferredRole = "SECOND_MARKER";
            if (c.status === "MODERATION_REQUIRED" || c.status === "MODERATED") inferredRole = "MODERATOR";
            const action = caseAction(c.status, inferredRole);
            const ActionIcon = action.icon;
            const isLocked = action.variant === "ghost";

            return (
              <div
                key={c.id}
                data-testid={`queue-row-${c.id}`}
                className="flex items-center gap-4 px-5 py-4 rounded-xl border border-slate-800 bg-slate-900/60 hover:bg-slate-800/40 transition-colors group"
              >
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isLocked ? "bg-slate-800" : "bg-sky-950/60 border border-sky-800/40"}`}>
                  {isLocked
                    ? <Lock className="w-4 h-4 text-slate-500" />
                    : <ClipboardList className="w-4 h-4 text-sky-400" />
                  }
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <StatusBadge status={c.status} />
                    {c.resolved_mark != null && (
                      <span className="text-xs text-emerald-400 font-medium">
                        {c.resolved_mark} / {c.max_mark}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 font-mono">
                    Case: {c.id?.slice(0, 20)}…
                    {c.attempt_id && (
                      <span className="ml-3">Attempt: {c.attempt_id?.slice(0, 16)}…</span>
                    )}
                  </div>
                </div>

                <button
                  data-testid={`open-workspace-${c.id}`}
                  onClick={() => openWorkspace(c.id, c.status)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                    action.variant === "primary"
                      ? "bg-sky-600 hover:bg-sky-500 text-white"
                      : action.variant === "amber"
                      ? "bg-amber-700/40 hover:bg-amber-700/60 border border-amber-700/40 text-amber-300"
                      : "border border-slate-700 text-slate-400 hover:border-slate-600 hover:text-slate-200"
                  }`}
                >
                  <ActionIcon className="w-3.5 h-3.5" />
                  {action.label}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Integrity note */}
      {!isCoordinator && (
        <div className="mt-6 flex items-start gap-2 text-xs text-slate-600 bg-slate-900/40 border border-slate-800/60 rounded-lg px-4 py-3">
          <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>
            You only see cases assigned to you. When independent marking applies, the primary
            marker's awarded mark is hidden until you submit your own round.
          </span>
        </div>
      )}
    </EvaluationShell>
  );
}
